import type React from "react"
import { useTranslation } from "react-i18next"

import { Decimal } from "@/components/decimal/decimal"
import { useUserSettings } from "@/contexts"
import { useFormatCurrency, useFormatDateRange } from "@/formatters"
import type { SelectableLanguageCode } from "@/models/i18n"
import { StackedBarGraphTooltip } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph-tooltip"
import { StackedBarGraphTooltipRow } from "@/nzds/graphs/stacked-bar-graph/stacked-bar-graph-tooltip-row"
import { HStack } from "@/nzds/layout"
import { PERIOD_DATE_FORMAT } from "@/utils/date"
import { translateDecimal } from "@/utils/decimal"
import { formatUsage } from "@/utils/format-usage/format-usage"
import { getDisplayValue } from "@/utils/value"

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import { Stack, useTheme } from "@mui/material"

import type {
  SiteBillsChartAmount,
  SiteBillsMonthMeterDetails,
  SiteBillsYearOverYear,
} from "./site-bills-monthly-series"

interface SiteBillsChartTooltipPayloadItem {
  color?: string
  dataKey?: number | string
  payload?: { xaxis?: string }
}

interface SiteBillsMonthlyChartTooltipProps {
  active?: boolean
  detailsByMonth: Record<string, Record<string, SiteBillsMonthMeterDetails>>
  payload?: SiteBillsChartTooltipPayloadItem[]
}

interface SiteBillsMeterDetailsProps {
  details: SiteBillsMonthMeterDetails
  includeYearOverYear?: boolean
  swatchColor?: string
}

const percentFormat = { precision: 0, style: "percent" } as const

const YearOverYearValue = ({ change }: { change: SiteBillsYearOverYear }) => {
  const { i18n, t } = useTranslation("sites")
  const theme = useTheme()
  const { unitsSystem } = useUserSettings()
  const locale = i18n.language as SelectableLanguageCode

  if (change.kind === "unavailable") {
    return getDisplayValue(null)
  }

  const options = {
    unitsSystem,
    useAccountingFormatWhenNegative: false,
  }
  const formatted = translateDecimal({
    format: percentFormat,
    localeCode: locale,
    options,
    value: change.ratio,
  })
  const zero = translateDecimal({
    format: percentFormat,
    localeCode: locale,
    options,
    value: 0,
  })
  const showArrow = formatted !== zero
  const rising = change.thisYearIsGreater
  const color = rising
    ? theme.palette.spectrum.red[400]
    : theme.palette.spectrum.green[400]
  const iconSx = {
    display: "block",
    fontSize: "inherit",
    height: "1em",
    width: "1em",
  } as const

  return (
    <HStack
      component="span"
      sx={{ alignItems: "center", color: showArrow ? color : undefined }}
    >
      {showArrow && rising ? (
        <ArrowUpwardIcon
          titleAccess={t("bills.chart.yearOverYear.increased")}
          sx={iconSx}
        />
      ) : null}
      {showArrow && !rising ? (
        <ArrowDownwardIcon
          titleAccess={t("bills.chart.yearOverYear.decreased")}
          sx={iconSx}
        />
      ) : null}
      <Decimal
        format={percentFormat}
        useAccountingFormatWhenNegative={false}
        value={change.ratio}
      />
    </HStack>
  )
}

const AmountLines = ({
  amounts,
  formatValue,
}: {
  amounts: readonly SiteBillsChartAmount[]
  formatValue: (amount: SiteBillsChartAmount) => string
}) => (
  <Stack>
    {amounts.map((amount) => (
      <span key={`${amount.label}:${amount.amount}`}>
        {formatValue(amount)}
      </span>
    ))}
  </Stack>
)

export const SiteBillsMeterDetails: React.FC<SiteBillsMeterDetailsProps> = ({
  details,
  includeYearOverYear = false,
  swatchColor,
}) => {
  const { i18n, t } = useTranslation("sites")
  const { formatCurrency } = useFormatCurrency(
    {},
    { useAccountingFormatWhenNegative: false }
  )
  const { formatDateRange } = useFormatDateRange(PERIOD_DATE_FORMAT)
  const { unitsSystem } = useUserSettings()
  const locale = i18n.language as SelectableLanguageCode

  return (
    <Stack spacing={1}>
      <StackedBarGraphTooltipRow
        label={t("fields.period.title", { ns: "common" })}
        value={
          <Stack>
            {details.periods.map((period) => (
              <span key={period.id}>
                {formatDateRange(period.startDate, period.endDate)}
              </span>
            ))}
          </Stack>
        }
      />
      <StackedBarGraphTooltipRow
        label={t("fields.meterCode.title", { ns: "common" })}
        swatchColor={swatchColor}
        value={details.meterCode}
      />
      <StackedBarGraphTooltipRow
        label={t("bills.chart.usage")}
        value={
          <AmountLines
            amounts={details.usages}
            formatValue={(amount) =>
              formatUsage(amount.amount, amount.label, locale, unitsSystem)
            }
          />
        }
      />
      {includeYearOverYear ? (
        <StackedBarGraphTooltipRow
          label={t("bills.chart.yearOverYear.usage")}
          value={<YearOverYearValue change={details.yearOverYear.usage} />}
        />
      ) : null}
      <StackedBarGraphTooltipRow
        label={t("bills.chart.cost")}
        value={
          <AmountLines
            amounts={details.costs}
            formatValue={(amount) =>
              formatCurrency(amount.amount, { currency: amount.label })
            }
          />
        }
      />
      {includeYearOverYear ? (
        <StackedBarGraphTooltipRow
          label={t("bills.chart.yearOverYear.cost")}
          value={<YearOverYearValue change={details.yearOverYear.cost} />}
        />
      ) : null}
    </Stack>
  )
}

export const SiteBillsMonthlyChartTooltip: React.FC<
  SiteBillsMonthlyChartTooltipProps
> = ({ active = false, detailsByMonth, payload }) => {
  const hovered = payload?.[0]
  const monthKey = hovered?.payload?.xaxis
  const meterId = hovered?.dataKey == null ? null : String(hovered.dataKey)
  const details =
    monthKey && meterId ? detailsByMonth[monthKey]?.[meterId] : undefined

  if (!active || !details || !monthKey) {
    return null
  }

  return (
    <StackedBarGraphTooltip>
      <SiteBillsMeterDetails
        details={details}
        includeYearOverYear
        swatchColor={hovered?.color}
      />
    </StackedBarGraphTooltip>
  )
}
